import { handleActions } from 'redux-actions';
import * as types from './constants';

export const AdminDashboardState = {
    overview: null,
    overviewLoading: false,
    overviewError: null,
    users: null,
    usersLoading: false,
    usersError: null,
    events: null,
    eventsLoading: false,
    eventsError: null,
    // Drill-down state is keyed by nominee id so a slow load for one nominee
    // doesn't overwrite a completed load for another.
    nomineeVotesById: {},
    nomineeVotesLoadingById: {},
    nomineeVotesErrorById: {},
    // Promo-code generation lifecycle. `justCreated` clears itself when the
    // modal closes so a stale success toast doesn't reappear on reopen.
    promoCreateLoading: false,
    promoCreateError: null,
    promoJustCreated: null
};

// Small helpers keep the reducer readable — the merge pattern is repeated for
// every nominee-scoped field and dedicated helpers avoid drift.
const withKeyedValue = (existing, key, value) => ({ ...existing, [key]: value });
const withoutKey = (existing, key) => {
    if (!(key in existing)) return existing;
    const next = { ...existing };
    delete next[key];
    return next;
};

export default handleActions(
    {
        [types.LOAD_OVERVIEW]: (state, action) => ({
            ...state,
            overview: action.payload,
            overviewError: null
        }),
        [types.LOAD_USERS]: (state, action) => ({
            ...state,
            users: action.payload,
            usersError: null
        }),
        [types.LOAD_EVENTS]: (state, action) => ({
            ...state,
            events: action.payload,
            eventsError: null
        }),
        [types.SET_OVERVIEW_LOADING]: (state, action) => ({
            ...state,
            overviewLoading: action.payload
        }),
        [types.SET_USERS_LOADING]: (state, action) => ({
            ...state,
            usersLoading: action.payload
        }),
        [types.SET_EVENTS_LOADING]: (state, action) => ({
            ...state,
            eventsLoading: action.payload
        }),
        [types.SET_OVERVIEW_ERROR]: (state, action) => ({
            ...state,
            overviewError: action.payload
        }),
        [types.SET_USERS_ERROR]: (state, action) => ({
            ...state,
            usersError: action.payload
        }),
        [types.SET_EVENTS_ERROR]: (state, action) => ({
            ...state,
            eventsError: action.payload
        }),
        [types.LOAD_NOMINEE_VOTES]: (state, action) => ({
            ...state,
            nomineeVotesById: withKeyedValue(
                state.nomineeVotesById,
                action.payload.nomineeId,
                action.payload.data
            ),
            nomineeVotesErrorById: withoutKey(
                state.nomineeVotesErrorById,
                action.payload.nomineeId
            )
        }),
        [types.SET_NOMINEE_VOTES_LOADING]: (state, action) => ({
            ...state,
            nomineeVotesLoadingById: withKeyedValue(
                state.nomineeVotesLoadingById,
                action.payload.nomineeId,
                !!action.payload.loading
            )
        }),
        [types.SET_NOMINEE_VOTES_ERROR]: (state, action) => ({
            ...state,
            nomineeVotesErrorById: withKeyedValue(
                state.nomineeVotesErrorById,
                action.payload.nomineeId,
                action.payload.error
            )
        }),
        [types.CLEAR_NOMINEE_VOTES]: (state, action) => ({
            ...state,
            nomineeVotesById: withoutKey(state.nomineeVotesById, action.payload.nomineeId),
            nomineeVotesErrorById: withoutKey(
                state.nomineeVotesErrorById,
                action.payload.nomineeId
            ),
            nomineeVotesLoadingById: withoutKey(
                state.nomineeVotesLoadingById,
                action.payload.nomineeId
            )
        }),
        [types.SET_PROMO_CREATE_LOADING]: (state, action) => ({
            ...state,
            promoCreateLoading: action.payload
        }),
        [types.SET_PROMO_CREATE_ERROR]: (state, action) => ({
            ...state,
            promoCreateError: action.payload
        }),
        [types.PROMO_CREATED]: (state, action) => ({
            ...state,
            promoJustCreated: action.payload,
            promoCreateError: null
        }),
        [types.CLEAR_PROMO_CREATED]: (state) => ({
            ...state,
            promoJustCreated: null,
            promoCreateError: null
        })
    },
    AdminDashboardState
);
